import React from "react";
import { Link } from "react-router-dom";
import { Button } from "reactstrap";
import {
	canExpand,
	canGenerate,
	catLabel,
	deploymentStatusPillKey,
	fmtDate,
	normalizeWritingJobStatusPill,
	normalizeChangesForExpand,
	rollupDeploymentStatus,
	rollupValidationStatus,
	validationStatusPillKey,
} from "./recommendationGridUtils";

const JOB_ICONS = {
	none: "fa-regular fa-circle",
	pending: "fa-regular fa-clock",
	in_progress: "fa-solid fa-spinner",
	completed: "fa-solid fa-circle-check",
	failed: "fa-solid fa-circle-exclamation",
};

const COL_COUNT = 12;

function SoftBadge({ kind, value }) {
	if (!value && kind === "severity") {
		return <span className="rec-grid__badge rec-grid__badge--severity-none">—</span>;
	}
	if (!value) return <span className="text-muted">—</span>;
	const cls =
		kind === "cat"
			? "rec-grid__badge--cat"
			: `rec-grid__badge--${kind}-${String(value).toLowerCase()}`;
	return <span className={`rec-grid__badge ${cls}`}>{catLabel(value)}</span>;
}

function JobStatusPill({ status, label }) {
	const raw = String(status || "none").toLowerCase() || "none";
	const st = normalizeWritingJobStatusPill(status);
	const text = label || (st === "none" ? "No job" : catLabel(raw));
	return (
		<span className={`rec-grid__job-pill rec-grid__job-pill--${st}`}>
			<i className={JOB_ICONS[st] || JOB_ICONS.none} aria-hidden />
			{text}
		</span>
	);
}

function DeploymentStatusCell({ item }) {
	const { status, label } = rollupDeploymentStatus(item);
	if (!status) return <span className="text-muted">—</span>;
	const st = deploymentStatusPillKey(status);
	return (
		<span className={`rec-grid__job-pill rec-grid__job-pill--${st}`}>
			{label || catLabel(status)}
		</span>
	);
}

function ValidationStatusCell({ item }) {
	const { status, label } = rollupValidationStatus(item);
	if (!status) return <span className="text-muted">—</span>;
	const st = validationStatusPillKey(status);
	return (
		<span className={`rec-grid__job-pill rec-grid__job-pill--${st}`}>
			{label || catLabel(status)}
		</span>
	);
}

function ExpandPanel({ item }) {
	const actions = normalizeChangesForExpand(item);
	return (
		<tr className="rec-grid__instructions-row">
			<td colSpan={COL_COUNT}>
				<div className="rec-grid__instructions-inner">
					<h6>
						<i className="fas fa-pen-to-square me-1" />
						Proposed edits ({item.writing_job_status_label || "job"})
					</h6>
					{actions.map((a, i) => {
						if (a.fallback) {
							return (
								<div key={i} className="rec-grid__reasoning">
									{a.fallback.split("\n").map((line, li) => (
										<span key={li}>
											{line}
											<br />
										</span>
									))}
								</div>
							);
						}
						return (
							<div key={i} className="rec-grid__action-card">
								<div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
									<span className="rec-grid__action-label">
										Action {a.index}
										{a.operation ? ` · ${a.operation}` : ""}
										{a.htmlTag ? ` · ${a.htmlTag}` : ""}
									</span>
									<div className="d-flex flex-wrap gap-1">
										{a.deploymentStatus ? (
											<span
												className={`rec-grid__job-pill rec-grid__job-pill--${deploymentStatusPillKey(a.deploymentStatus)}`}
											>
												{catLabel(a.deploymentStatus)}
											</span>
										) : null}
										{a.validationStatus ? (
											<span
												className={`rec-grid__job-pill rec-grid__job-pill--${validationStatusPillKey(a.validationStatus)}`}
												title={a.validationExplanation || undefined}
											>
												{catLabel(a.validationStatus)}
											</span>
										) : null}
									</div>
								</div>
								{a.oldContent ? (
									<div className="mb-1">
										<span className="rec-grid__old">{a.oldContent}</span>
									</div>
								) : null}
								{a.newContent ? (
									<>
										<div className="mb-2">
											<i
												className="fas fa-arrow-down-long text-muted me-1"
												style={{ fontSize: "0.7rem" }}
											/>
										</div>
										<div className="mb-2">
											<span className="rec-grid__new">{a.newContent}</span>
										</div>
									</>
								) : null}
								{a.why ? (
									<div className="rec-grid__reasoning mb-1">
										<strong>Why it matters:</strong> {a.why}
									</div>
								) : null}
								{a.reasoning ? (
									<div className="rec-grid__reasoning">
										<strong>Reasoning:</strong> {a.reasoning}
									</div>
								) : null}
							</div>
						);
					})}
				</div>
			</td>
		</tr>
	);
}

export default function RecommendationRow({
	item,
	rowId,
	expanded,
	actionLoading,
	onToggleExpand,
	onView,
	onGenerate,
}) {
	const selectable = Boolean(item.selectable);
	const expandible = canExpand(item);
	const generating = actionLoading === `generate-${rowId}`;
	const auditId = item.audit_id;

	let actionsCell;
	if (!selectable) {
		actionsCell = (
			<span className="rec-grid__flag">
				<i className="fas fa-flag" />
				NOT_CONTENT_TEAM
			</span>
		);
	} else {
		const buttons = [];
		if (item.writing_job_id != null) {
			buttons.push(
				<Button
					key="view"
					color="light"
					size="sm"
					className="rec-grid__action-btn border"
					onClick={() => onView(item)}
				>
					<i className="far fa-eye me-1" />
					View
				</Button>
			);
		} else if (canGenerate(item)) {
			buttons.push(
				<Button
					key="gen"
					color="primary"
					outline
					size="sm"
					className="rec-grid__action-btn"
					disabled={generating}
					onClick={() => onGenerate(item, rowId)}
				>
					{generating ? (
						<>
							<i className="fas fa-spinner fa-spin me-1" />
							Generating…
						</>
					) : (
						<>
							<i className="fas fa-wand-magic-sparkles me-1" />
							Generate fix
						</>
					)}
				</Button>
			);
		}
		actionsCell = <div className="rec-grid__actions-cell">{buttons}</div>;
	}

	return (
		<>
			<tr className={!selectable ? "rec-grid__row--disabled" : undefined}>
				<td>
					{expandible ? (
						<button
							type="button"
							className={`rec-grid__expand-btn${expanded ? " rec-grid__expand-btn--open" : ""}`}
							onClick={() => onToggleExpand(rowId)}
							aria-label={expanded ? "Collapse" : "Expand"}
						>
							<i className="fas fa-chevron-right" />
						</button>
					) : null}
				</td>
				<td>
					<div className="rec-grid__rec-title">{item.title}</div>
					{auditId ? (
						<div className="rec-grid__rec-sub">
							<Link
								to={`/aivisibility/report?auditId=${encodeURIComponent(auditId)}`}
								className="rec-grid__audit-link"
								title="View audit report"
							>
								Audit Report
							</Link>
						</div>
					) : null}
				</td>
				<td>
					<SoftBadge kind="cat" value={item.category} />
				</td>
				<td>
					<SoftBadge kind="severity" value={item.severity} />
				</td>
				<td>
					<SoftBadge kind="impact" value={item.impact} />
				</td>
				<td>
					<SoftBadge kind="effort" value={item.effort} />
				</td>
				<td>
					{item.url ? (
						<div className="rec-grid__url-cell">
							<a
								href={item.url}
								target="_blank"
								rel="noopener noreferrer"
								title={item.url}
								className="rec-grid__page-visit"
							>
								{item.page_type
									? catLabel(item.page_type)
									: "Visit affected page"}
								<i className="fas fa-external-link-alt ms-1" aria-hidden />
							</a>
						</div>
					) : (
						<span className="rec-grid__site-wide">Site-wide</span>
					)}
				</td>
				<td>
					<JobStatusPill
						status={item.writing_job_status}
						label={item.writing_job_status_label}
					/>
				</td>
				<td>
					<DeploymentStatusCell item={item} />
				</td>
				<td>
					<ValidationStatusCell item={item} />
				</td>
				<td>{fmtDate(item.created_at)}</td>
				<td>{actionsCell}</td>
			</tr>
			{expanded && expandible ? <ExpandPanel item={item} /> : null}
		</>
	);
}
